import { DEFAULT_MEAL_GROUP, parseAnchor, uid, workoutToday } from './state';

/* ── Canonical food helpers ─────────────────────────────────────────────────
 * A food is { id, name, brand, unit:'g'|'serving', per:{kcal,protein,carbs,fats}
 *            (per 1 unit), portion (default amount), serving (label), source, code }.
 * A logged entry is { id, name, group, unit, per, amount, source, brand }.
 * Totals = per × amount, so grams (Open Food Facts, per-100g→per-gram) and
 * per-serving manual foods share one code path. */
export function entryMacros(e) {
  const per = e.per || {};
  const amt = entryAmount(e);
  return {
    kcal: (per.kcal || 0) * amt,
    protein: (per.protein || 0) * amt,
    carbs: (per.carbs || 0) * amt,
    fats: (per.fats || 0) * amt,
  };
}

/* An entry's amount; very old entries stored `servings` instead. */
export function entryAmount(e) {
  return e.amount != null ? e.amount : (e.servings != null ? e.servings : 1);
}

/* 'g' and 'ml' are both "per-100-units" foods (weight- or volume-based);
 * anything else (e.g. 'serving') is a per-1-unit food. */
export function isMassUnit(unit) { return unit === 'g' || unit === 'ml'; }

/* The amount a food is logged at when nothing else is chosen. */
export function foodPortion(food) {
  if (food.portion != null) return food.portion;
  return isMassUnit(food.unit) ? 100 : 1;
}

/* `portion` x `scale`, rounded to whole grams or to 0.01 serving. */
export function scalePortion(food, scale) {
  const v = (Number(foodPortion(food)) || 0) * scale;
  return isMassUnit(food.unit) ? Math.round(v) : Math.round(v * 100) / 100;
}

/* Macros for a food's reference amount (100 g/ml, or 1 serving) — for list rows. */
export function foodRefMacros(food) {
  const mass = isMassUnit(food.unit);
  const amt = mass ? 100 : 1;
  const p = food.per || {};
  return {
    amt, unit: mass ? food.unit : 'serving',
    kcal: (p.kcal || 0) * amt, protein: (p.protein || 0) * amt, carbs: (p.carbs || 0) * amt, fats: (p.fats || 0) * amt,
  };
}

/* Open Food Facts result → canonical food (per-100 → per-unit). off.unit is
 * 'g' or 'ml', detected server-side from OFF category/quantity metadata. */
export function offToFood(off) {
  const p = off.per100 || {};
  const per = {
    kcal: (Number(p.kcal) || 0) / 100, protein: (Number(p.protein) || 0) / 100,
    carbs: (Number(p.carbs) || 0) / 100, fats: (Number(p.fats) || 0) / 100,
  };
  const name = off.brand ? `${off.name} (${off.brand})` : off.name;
  const unit = off.unit === 'ml' ? 'ml' : 'g';
  const servingAmt = off.serving_g && off.serving_g > 0 ? off.serving_g : null;
  return {
    id: off.code ? `food-off-${off.code}` : uid('food'),
    name, brand: off.brand || '', unit, per,
    portion: servingAmt || 100,
    serving: servingAmt ? `${Math.round(servingAmt)} ${unit} serving` : `per 100 ${unit}`,
    source: 'off', code: off.code || '',
  };
}

/* A logged entry back in canonical food shape, carrying the portion it was
 * last logged at — so re-logging it is a single tap at the usual amount. */
function entryToFood(e, id) {
  return {
    id,
    name: e.name, brand: e.brand || '',
    unit: e.unit || 'serving',
    per: e.per || { kcal: 0, protein: 0, carbs: 0, fats: 0 },
    portion: entryAmount(e),
    serving: '', source: e.source || 'recent', code: e.code || '',
  };
}

/* Foods you eat often *in this meal group*, best first.
 *
 * Scored with an age decay (0.97^days, ~23-day half-life) so the list tracks
 * what you're eating now rather than what you ate months ago, and scoped to a
 * single group on purpose: the eggs you have every breakfast shouldn't crowd
 * the top of your dinner list. A food logged once isn't a habit, so it takes
 * two logs to appear. */
export function getFrequentFoods(nutrition, group, limit = 6) {
  if (!group) return [];
  const today = workoutToday();
  const byName = new Map(); // lower(name) -> { food, score, count, lastDay }

  for (const [day, dayEntries] of Object.entries(nutrition || {})) {
    const d = parseAnchor(day);
    if (!d) continue;
    const daysAgo = Math.round((today - d) / 86400000);
    if (daysAgo < 0 || daysAgo > 120) continue;
    const weight = Math.pow(0.97, daysAgo);

    for (const e of dayEntries || []) {
      if ((e.group || DEFAULT_MEAL_GROUP) !== group) continue;
      const nkey = (e.name || '').trim().toLowerCase();
      if (!nkey) continue;
      const prev = byName.get(nkey);
      // Keep the most recent version of the food — that portion is the one to re-log.
      const fresher = !prev || day > prev.lastDay;
      byName.set(nkey, {
        food: fresher ? entryToFood(e, `freq-${group}-${nkey}`) : prev.food,
        score: (prev ? prev.score : 0) + weight,
        count: (prev ? prev.count : 0) + 1,
        lastDay: fresher ? day : prev.lastDay,
      });
    }
  }

  return [...byName.values()]
    .filter(v => v.count >= 2)
    .sort((a, b) => b.score - a.score || b.lastDay.localeCompare(a.lastDay))
    .slice(0, limit)
    .map(v => v.food);
}

/* Habit score per food name for this group, used to float familiar foods to
 * the top of search results. Same decay as getFrequentFoods. */
export function getGroupFoodScores(nutrition, group) {
  const scores = new Map(); // lower(name) -> number
  if (!group) return scores;
  const today = workoutToday();
  for (const [day, dayEntries] of Object.entries(nutrition || {})) {
    const d = parseAnchor(day);
    if (!d) continue;
    const daysAgo = Math.round((today - d) / 86400000);
    if (daysAgo < 0 || daysAgo > 120) continue;
    const weight = Math.pow(0.97, daysAgo);
    for (const e of dayEntries || []) {
      if ((e.group || DEFAULT_MEAL_GROUP) !== group) continue;
      const nkey = (e.name || '').trim().toLowerCase();
      if (!nkey) continue;
      scores.set(nkey, (scores.get(nkey) || 0) + weight);
    }
  }
  return scores;
}

/* Most-recently logged distinct foods, newest first — for one-tap re-logging. */
export function getRecentFoods(nutrition, limit = 10) {
  const seen = new Set();
  const out = [];
  const days = Object.keys(nutrition || {}).sort((a, b) => b.localeCompare(a));
  for (const day of days) {
    const dayEntries = nutrition[day] || [];
    for (let i = dayEntries.length - 1; i >= 0; i -= 1) {
      const e = dayEntries[i];
      const nkey = (e.name || '').toLowerCase();
      if (!nkey || seen.has(nkey)) continue;
      seen.add(nkey);
      out.push(entryToFood(e, `recent-${nkey}`));
      if (out.length >= limit) return out;
    }
  }
  return out;
}

/* Fold one food into a state object: append the log entry for `group`, and
 * upsert the food into the reusable library so it's an instant local match
 * (and a Recent) next time. Pure, so a template can apply every one of its
 * items in a single state update instead of one render per item.
 * opts.id: the new entry's id (so a caller can undo exactly this add).
 * opts.at: epoch ms stored as the entry's `at`; callers pass it only for
 * today, because a time on a back-filled day would be made up. */
export function logFoodInto(prev, food, dayKey, group, opts = {}) {
  const name = (food.name || '').trim();
  if (!name) return prev;
  const unit = isMassUnit(food.unit) ? food.unit : 'serving';
  const per = food.per || { kcal: 0, protein: 0, carbs: 0, fats: 0 };
  const amount = food.portion != null ? food.portion : (unit === 'serving' ? 1 : 100);
  const list = [...(prev.foods || [])];
  const nkey = name.toLowerCase();
  const idx = list.findIndex(f => (f.name || '').toLowerCase() === nkey && (f.unit || 'serving') === unit);
  const existing = idx >= 0 ? list[idx] : {};
  // A re-log by name (Recent, a template, a copy, typed) carries no barcode;
  // keep the one the library has so the next scan still resolves locally.
  const code = food.code || existing.code || '';
  const brand = food.brand || existing.brand || '';
  const entry = {
    id: opts.id || uid('meal'), name, group: group || DEFAULT_MEAL_GROUP,
    unit, per, amount, source: food.source || 'manual', brand, code,
  };
  if (Number.isFinite(opts.at)) entry.at = opts.at;
  const rec = {
    ...existing, // preserve favorite (and any future fields) across re-logs
    id: idx >= 0 ? list[idx].id : (typeof food.id === 'string' && food.id.startsWith('food-') ? food.id : uid('food')),
    name, brand, unit, per, portion: amount,
    serving: food.serving || existing.serving || '', source: food.source || 'manual', code,
  };
  if (idx >= 0) list[idx] = rec; else list.push(rec);
  return {
    ...prev,
    foods: list,
    nutrition: { ...prev.nutrition, [dayKey]: [...(prev.nutrition[dayKey] || []), entry] },
  };
}

/* Log several foods into one day and group in one fold: new entry ids, the
 * same amounts (times `scale`) and one shared `at`. Templates and copied
 * meals both go through here. */
export function logItemsInto(prev, items, dayKey, group, { at, scale = 1 } = {}) {
  return (items || []).reduce((acc, item) => logFoodInto(
    acc, scale === 1 ? item : { ...item, portion: scalePortion(item, scale) }, dayKey, group, { at },
  ), prev);
}

/* Change a logged entry's amount. The library food with the same name+unit
 * takes it as its portion too, so the next add from search or Favorites
 * starts at the corrected amount (Your usual and Recent already do). An
 * emptied field (0) only touches the entry. */
export function setEntryAmount(prev, dayKey, entryId, amount) {
  const value = Math.max(0, Number(amount) || 0);
  const day = (prev.nutrition || {})[dayKey] || [];
  const target = day.find(e => e.id === entryId);
  if (!target) return prev;
  const next = {
    ...prev,
    nutrition: { ...prev.nutrition, [dayKey]: day.map(e => e.id === entryId ? { ...e, amount: value } : e) },
  };
  if (value > 0 && Array.isArray(prev.foods)) {
    const nkey = (target.name || '').trim().toLowerCase();
    const unit = isMassUnit(target.unit) ? target.unit : 'serving';
    const match = f => (f.name || '').toLowerCase() === nkey && (f.unit || 'serving') === unit && f.portion !== value;
    if (prev.foods.some(match)) next.foods = prev.foods.map(f => match(f) ? { ...f, portion: value } : f);
  }
  return next;
}

/* Entries of one meal group (entries without a group count as snacks). */
export function groupEntries(entries, group) {
  return (entries || []).filter(e => (e.group || DEFAULT_MEAL_GROUP) === group);
}

/* kcal/macros, count and the first two names of a list of entries, for the
 * one-line previews of a meal to copy. */
export function summarizeEntries(entries) {
  const list = entries || [];
  const sum = list.reduce((acc, e) => {
    const m = entryMacros(e);
    return { kcal: acc.kcal + m.kcal, protein: acc.protein + m.protein, carbs: acc.carbs + m.carbs, fats: acc.fats + m.fats };
  }, { kcal: 0, protein: 0, carbs: 0, fats: 0 });
  return { ...sum, count: list.length, names: list.slice(0, 2).map(e => e.name) };
}

/* The latest `limit` days (newest first) with something logged in `group`
 * (any group when null), leaving out `excludeKey` (the day being copied
 * into) and anything after `maxKey`. */
export function recentMealDays(nutrition, { group = null, excludeKey = null, maxKey = null, limit = 7 } = {}) {
  const out = [];
  const days = Object.keys(nutrition || {})
    .filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k) && k !== excludeKey && (!maxKey || k <= maxKey))
    .sort((a, b) => b.localeCompare(a));
  for (const date of days) {
    const entries = group ? groupEntries(nutrition[date], group) : (nutrition[date] || []);
    if (!entries.length) continue;
    out.push({ date, entries, ...summarizeEntries(entries) });
    if (out.length >= limit) break;
  }
  return out;
}

/* Copy every entry of `fromKey` into `toKey`, each into its own group, with
 * new ids. 'replace' first empties the target day; 'append' keeps it. */
export function copyDayInto(prev, fromKey, toKey, { mode = 'append', at } = {}) {
  const source = (prev.nutrition || {})[fromKey] || [];
  if (!source.length || fromKey === toKey) return prev;
  const base = mode === 'replace' ? { ...prev, nutrition: { ...prev.nutrition, [toKey]: [] } } : prev;
  return source.reduce((acc, e) => logFoodInto(
    acc, entriesToTemplateItems([e])[0], toKey, e.group || DEFAULT_MEAL_GROUP, { at },
  ), base);
}

/* Barcode -> { food, usualAmount, date } from Mo's own data, so a product
 * he has logged before resolves without a network call. The latest entry
 * with the code wins: its amount is the one he actually ate (corrected after
 * logging), and the library record with that code supplies the food's id and
 * serving label. Library foods with a code but no entry fill in the rest. */
export function buildBarcodeIndex(nutrition, foods) {
  const latest = new Map(); // code -> { entry, date }
  for (const date of Object.keys(nutrition || {}).sort()) {
    for (const e of nutrition[date] || []) {
      const code = String(e.code || '').trim();
      if (code && (e.name || '').trim()) latest.set(code, { entry: e, date });
    }
  }
  const byCode = new Map();
  for (const f of foods || []) {
    const code = String(f.code || '').trim();
    if (code && (f.name || '').trim() && !byCode.has(code)) byCode.set(code, f);
  }
  const index = new Map();
  for (const [code, { entry, date }] of latest) {
    const lib = byCode.get(code);
    const food = lib && String(lib.name).toLowerCase() === String(entry.name).toLowerCase()
      ? { ...lib, per: entry.per || lib.per, code }
      // Not a 'food-' id: logFoodInto then never reuses an id the library
      // already holds under another name.
      : { ...entryToFood(entry, `scan-${code}`), code };
    index.set(code, { food, usualAmount: entryAmount(entry), date });
  }
  for (const [code, f] of byCode) {
    if (!index.has(code)) index.set(code, { food: f, usualAmount: foodPortion(f), date: null });
  }
  return index;
}

/* -- Meal templates -------------------------------------------------------
 * A template is a named set of foods you log together ("Oats + whey +
 * banana"). Items are stored in canonical food shape, so applying one runs
 * through the same logFood path as any other add: amounts stay editable
 * afterwards and the foods land in your library as usual.
 * Shape: { id, name, group, items: [food], createdAt, updatedAt? }. */

export function entriesToTemplateItems(entries) {
  return (entries || []).map(e => ({
    name: e.name, brand: e.brand || '', unit: e.unit || 'serving',
    per: e.per || { kcal: 0, protein: 0, carbs: 0, fats: 0 },
    portion: entryAmount(e),
    serving: e.serving || '', source: e.source || 'manual', code: e.code || '',
  }));
}

export function templateMacros(tpl, scale = 1) {
  return (tpl.items || []).reduce((acc, item) => {
    const m = entryMacros({ per: item.per, amount: scale === 1 ? item.portion : scalePortion(item, scale) });
    return {
      kcal: acc.kcal + m.kcal, protein: acc.protein + m.protein,
      carbs: acc.carbs + m.carbs, fats: acc.fats + m.fats,
    };
  }, { kcal: 0, protein: 0, carbs: 0, fats: 0 });
}

/* State with template `id` renamed and/or given new items, stamped with
 * updatedAt (ISO). Templates merge per id, so only that one changes. */
export function editTemplate(state, id, patch, updatedAt) {
  const list = state.mealTemplates || [];
  if (!list.some(x => x.id === id)) return state;
  const clean = {};
  if (typeof patch.name === 'string' && patch.name.trim()) clean.name = patch.name.trim().slice(0, 60);
  if (Array.isArray(patch.items)) clean.items = patch.items;
  return { ...state, mealTemplates: list.map(x => x.id === id ? { ...x, ...clean, updatedAt } : x) };
}
